import api from './client';
import type {
  PwaApp,
  CreatePwaInput,
  UpdatePwaInput,
  PwaStatsResponse,
  UploadUrlResponse,
  UploadResponse,
  PwaReviewItem,
} from './types';

export type PwaGenerateMode = 'all' | 'copy' | 'icon' | 'splash' | 'screenshot';

export interface PwaProject {
  id: string;
  name: string;
  prompt: string;
  status: 'active' | 'deleted';
  created_by_admin_id: string | null;
  created_at: string;
  updated_at: string;
}

export const getPwaProjects = (): Promise<PwaProject[]> =>
  api.get<PwaProject[]>('/admin/pwa/projects');

export const createPwaProject = (input: { name: string; prompt: string }): Promise<PwaProject> =>
  api.post<PwaProject>('/admin/pwa/projects', input);

export const updatePwaProject = (id: string, input: { name: string; prompt: string }): Promise<PwaProject> =>
  api.put<PwaProject>(`/admin/pwa/projects/${id}`, input);

export const deletePwaProject = (id: string): Promise<{ message: string }> =>
  api.delete<{ message: string }>(`/admin/pwa/projects/${id}`);

export interface GeneratedPwaDraft {
  app_name: string;
  developer_name: string;
  category: string;
  rating: number;
  reviews_count: number;
  downloads_count: string;
  size: string;
  version: string;
  short_description: string;
  full_description: string;
  icon_url: string;
  splash_url: string;
  bg_color: string;
  language_code: string;
  language_name: string;
  visual_direction: string;
  reviews: PwaReviewItem[];
  screenshots: string[];
  offer_url: string;
  store_layout: 'play' | 'app';
  screenshot_index?: number;
  screenshot_url?: string;
}

export async function generatePwaDraft(input: {
  project_id: string;
  platform: 'ios' | 'android';
  geo: string;
  mode?: PwaGenerateMode;
  screenshot_index?: number;
  draft?: Partial<GeneratedPwaDraft>;
}): Promise<Partial<GeneratedPwaDraft>> {
  return api.post<Partial<GeneratedPwaDraft>>('/admin/pwa/generate-draft', input);
}

export async function getPwaApps(): Promise<PwaApp[]> {
  return api.get<PwaApp[]>('/admin/pwa');
}

export async function getPwaApp(id: string): Promise<PwaApp> {
  return api.get<PwaApp>(`/admin/pwa/${id}`);
}

export async function createPwaApp(data: CreatePwaInput): Promise<PwaApp> {
  return api.post<PwaApp>('/admin/pwa', data);
}

export async function updatePwaApp(id: string, data: UpdatePwaInput): Promise<PwaApp> {
  return api.put<PwaApp>(`/admin/pwa/${id}`, data);
}

export async function deletePwaApp(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/admin/pwa/${id}`);
}

export async function getPwaStats(id: string): Promise<PwaStatsResponse> {
  return api.get<PwaStatsResponse>(`/admin/pwa/${id}/stats`);
}

export async function getPwaUploadUrl(
  filename: string,
  contentType: string
): Promise<UploadUrlResponse> {
  return api.post<UploadUrlResponse>('/admin/pwa/upload-url', {
    filename,
    content_type: contentType,
  });
}

export async function uploadPwaFile(
  uploadKey: string,
  file: File | Blob
): Promise<UploadResponse> {
  const arrayBuffer = await file.arrayBuffer();
  const contentType = file instanceof File ? file.type : 'application/octet-stream';
  return api.upload<UploadResponse>(`/admin/pwa/upload/${uploadKey}`, arrayBuffer, contentType);
}

export async function translateDescriptions(
  short_description: string,
  full_description: string,
  languages: string[]
): Promise<Record<string, { short: string; full: string }>> {
  return api.post<Record<string, { short: string; full: string }>>('/admin/pwa/translate', {
    short_description,
    full_description,
    languages,
  });
}

export async function translateReviews(
  reviews: { text: string; reply?: string }[],
  languages: string[]
): Promise<Record<string, { text: string; reply: string }[]>> {
  return api.post<Record<string, { text: string; reply: string }[]>>('/admin/pwa/translate-reviews', {
    reviews,
    languages,
  });
}

export async function uploadPwaMedia(file: File): Promise<string> {
  const { key } = await getPwaUploadUrl(file.name, file.type);
  const { url } = await uploadPwaFile(key, file);
  return url;
}
